import React, {CSSProperties, useMemo} from 'react'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub, HubLike, noBoardContainerId} from 'yougile-model'
import {DropdownContent} from '@components/common/dropdown/dropdown-content'
import {FlatTree} from '@components/common/flat-tree/flat-tree'
import {NoBoardIcon, TaskDoneIcon} from '@components/common/icons/icons'
import {Tree} from '@components/common/tree/tree'
import {TreeNode} from '@components/common/tree/tree-node'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import BoardIcon from './board.svg'
import ColumnIcon from './column.svg'
import ProjectIcon from './project.svg'

const getProjectTreeNodes = (
  includeTasks: boolean,
  filterTasks?: (task: Hub) => boolean,
  filterProjects?: (project: Hub) => boolean
) => {
  const companyId = globals.getCompanyId()
  const allProjects = Project.listProjects(companyId, 'not-archive').sort(
    Project.projectOrderComparer
  )
  const filteredProjects = filterProjects
    ? allProjects.filter(filterProjects)
    : allProjects

  const getTasks = (column: HubLike) => {
    const allTasks = Column.listTasks(column, false, {
      sorted: true,
      withoutArchive: true
    })
    const filteredTasks = filterTasks ? allTasks.filter(filterTasks) : allTasks

    return filteredTasks.map(x => {
      const done = x.data.gtd?.state?.stage === 'done'
      return {
        value: x.id,
        label: x.title,
        icon: <TaskDoneIcon done={done} />,
        className: done ? 'text-obscure-old line-through' : undefined,
        selectable: true
      } as TreeNode<DataObjectId>
    })
  }

  const getColumns = (board: Hub) =>
    Board.listColumns(board, {sorted: true, noMirrors: true}).map(x => ({
      value: x.id,
      label: x.title,
      children: includeTasks ? getTasks(x) : [],
      selectable: !includeTasks,
      icon: <ColumnIcon />
    }))

  const tree = filteredProjects.map(project => {
    const children = Project.listBoards(project, true).map(board => {
      return {
        value: board.id,
        label: board.title,
        icon: <BoardIcon />,
        children: getColumns(board)
      }
    })
    return {
      value: project.id,
      icon: <ProjectIcon />,
      label: project.title,
      children
    }
  })

  const noBoard = {
    value: noBoardContainerId,
    icon: <NoBoardIcon />,
    label: Lang.msg.subtask.withoutBoard,
    children: getTasks(globals.getHubLike(noBoardContainerId))
  }

  return includeTasks ? [noBoard, ...tree] : tree
}

interface Props {
  onChange: (value: DataObjectId[]) => void
  className?: string
  treeStyle?: CSSProperties
  filterTasks?: (task: Hub) => boolean
  filterProjects?: (project: Hub) => boolean
  value: DataObjectId[]
  oneSelectedItem?: boolean
  onClose?: () => void
}

export const ProjectsTree = (props: Props) => {
  const tree = useMemo(
    () => getProjectTreeNodes(true, props.filterTasks, props.filterProjects),
    []
  )
  return (
    <Tree
      className={props.className}
      treeStyle={props.treeStyle}
      nodes={tree}
      value={props.value}
      queryPlaceholder={Lang.msg.component.tree.enterName}
      onChange={props.onChange}
      oneSelectedItem={props.oneSelectedItem}
      onClose={props.onClose}
      forceCustomScroll
    />
  )
}

interface ProjectFlatTreeProps {
  onChange: (value: DataObjectId) => void
  className?: string
  value?: DataObjectId
}

export const ProjectsFlatTree = (props: ProjectFlatTreeProps) => {
  const tree = useMemo(() => getProjectTreeNodes(false), [])
  return (
    <FlatTree
      width={240}
      nodesClassName="h-208 overflow-auto"
      className={props.className}
      nodes={tree}
      value={props.value}
      onChange={props.onChange}
      levels={[
        Lang.msg.chooseObjectProject,
        Lang.msg.chooseObjectBoard,
        Lang.msg.chooseObjectColumn
      ]}
    />
  )
}

export const ProjectsFlatTreeDropdownContent = (
  props: ProjectFlatTreeProps
) => (
  <DropdownContent
    className="border border-solid border-main-old shadow"
    disableHeaderMargin={true}
    header={Lang.msg.subtask.placement}
  >
    <ProjectsFlatTree {...props} />
  </DropdownContent>
)
